/**
 * Popup content component for place markers on the map
 * Uses in-memory Place data from PlacesDataContext
 */

import { use, useMemo } from 'react';
import { PlacePopupContent } from './PlacePopupContent';
import { PlacesDataContext } from '@/data/places/places-data-context';

interface GeographicEntityWithRelevance extends GeographicEntity {
    relevanceType?: string;
    relevanceReason?: string;
}

interface PlaceMarkerPopupProps {
    entity: GeographicEntityWithRelevance;
    bookName?: string;
    passageYear?: number | null;
}

export function PlaceMarkerPopup({ entity, bookName, passageYear }: PlaceMarkerPopupProps) {
    const placesContext = use(PlacesDataContext);

    // Look up full Place data from in-memory context
    // Try placeName first, then fall back to relatedPlaceNames
    const placeData = useMemo(() => {
        if (!placesContext) return undefined;

        // 1. Try exact placeName match
        let place = placesContext.getPlaceByName(entity.placeName);
        if (place) return place;

        // 2. Try relatedPlaceNames (if available)
        if (entity.relatedPlaceNames && entity.relatedPlaceNames.length > 0) {
            for (const relatedName of entity.relatedPlaceNames) {
                place = placesContext.getPlaceByName(relatedName);
                if (place) return place;
            }
        }

        return undefined;
    }, [placesContext, entity.placeName, entity.relatedPlaceNames]);

    // If we have full Place data, merge it with entity data
    // Entity data (passage-specific) takes precedence for certain fields
    if (placeData) {
        return (
            <PlacePopupContent
                placeName={entity.placeName}
                type={entity.type}
                // Prefer passage-specific data, fall back to Place data
                summary={entity.summary || placeData.summary}
                description={entity.description || placeData.description}
                passageRelevance={entity.passageRelevance} // Keep passage-specific
                biblicalRelevance={placeData.biblicalRelevance} // Add from Place
                modernReference={entity.modernReference || placeData.coordinates.modernReference}
                coordinates={{
                    precision: entity.coordinatePrecision || placeData.coordinates.precision,
                    confidence: entity.coordinateConfidence || placeData.coordinates.confidence
                }}
                importance={entity.importance || placeData.importance}
                // Add Place-specific rich data
                geography={placeData.geography}
                significance={placeData.significance}
                archaeology={placeData.archaeology}
                historicalPeriods={placeData.historicalPeriods}
                biblicalReferences={placeData.biblicalReferences}
                // Prefer entity's historical context if available, otherwise use Place periods
                historicalContext={entity.historicalContext && entity.historicalContext.length > 0
                    ? entity.historicalContext
                    : undefined}
                relatedHistoricalEvents={entity.relatedHistoricalEvents || placeData.relatedHistoricalEvents}
                sources={entity.sources || placeData.sources}
                verseRef={entity.verseRef}
                bookName={bookName}
                relevanceType={entity.relevanceType}
                relevanceReason={entity.relevanceReason}
                // Add place images from hydrated data
                ruinsImageUrl={placeData.images?.ruinsImageUrl}
                reconstructionsByPeriod={placeData.images?.reconstructionsByPeriod}
                // Pass passage year for period-specific image selection
                passageYear={passageYear}
                generatedBy={placeData.generation?.model || (placeData as any).generatedBy || (placeData as any).source}
            />
        );
    }

    // If no Place data found, show entity data only
    return (
        <PlacePopupContent
            placeName={entity.placeName}
            type={entity.type}
            summary={entity.summary}
            description={entity.description}
            passageRelevance={entity.passageRelevance}
            modernReference={entity.modernReference}
            coordinates={{
                precision: entity.coordinatePrecision,
                confidence: entity.coordinateConfidence
            }}
            importance={entity.importance}
            historicalContext={entity.historicalContext}
            relatedHistoricalEvents={entity.relatedHistoricalEvents}
            sources={entity.sources}
            verseRef={entity.verseRef}
            bookName={bookName}
            relevanceType={entity.relevanceType}
            relevanceReason={entity.relevanceReason}
            generatedBy={(entity as any).generatedBy || (entity as any).source}
        />
    );
}

